import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, ShieldCheck, User as UserIcon, Building2, Filter, Plus, Pencil, Trash2, Key, Sparkles, Copy, Check, Mail, Headset } from "lucide-react";
import { RECEPTIONIST_PERM_LIST } from "@/lib/agencyPerms";
import { AdminChannelsView } from "@/pages/admin/agency/ReceptionistChannels";

const ROLE_LABEL = { agency_admin: "Agenzia", agency_receptionist: "Receptionist", salon_owner: "Titolare salone", staff: "Staff" };
const AUTH_LABEL = { jwt: "Email/Password", google: "Google" };
const ROLE_COLOR = { agency_admin: "#8B5CF6", agency_receptionist: "#F59E0B", salon_owner: "#06B6D4", staff: "#3B82F6" };

export default function AgencyUsers() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showProvision, setShowProvision] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : []));
    // /admin/tenants restituisce {tenants:[...]} o array raw a seconda della versione
    api.get("/admin/tenants").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.tenants || r.data?.items || []);
      setTenants(list);
    });
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (q) {
        const hay = `${u.email} ${u.name} ${u.tenant_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, query, roleFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    owners: users.filter((u) => u.role === "salon_owner").length,
    google: users.filter((u) => u.auth_provider === "google").length,
    admins: users.filter((u) => u.role === "agency_admin").length,
  }), [users]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
  };

  return (
    <div data-testid="agency-users" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Account registrati</h1>
          <p className="text-[color:var(--sp-text-secondary)] mt-2">Tutti gli utenti di Memento — titolari saloni, staff, admin.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowProvision(true)} data-testid="new-tenant-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--sp-accent)] text-white font-bold hover:opacity-90">
            <Sparkles className="h-4 w-4" /> Nuovo salone
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="new-user-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--sp-brand)] text-white font-bold hover:opacity-90">
            <Plus className="h-4 w-4" /> Nuovo utente
          </button>
        </div>
      </header>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="sp-card p-4 sp-anim">
          <span className="label-eyebrow">Utenti totali</span>
          <div className="font-display text-2xl font-black mt-2">{stats.total}</div>
        </div>
        <div className="sp-card p-4 sp-anim">
          <span className="label-eyebrow">Titolari</span>
          <div className="font-display text-2xl font-black mt-2 text-[color:var(--sp-brand)]">{stats.owners}</div>
        </div>
        <div className="sp-card p-4 sp-anim">
          <span className="label-eyebrow">Google Auth</span>
          <div className="font-display text-2xl font-black mt-2">{stats.google}</div>
        </div>
        <div className="sp-card p-4 sp-anim">
          <span className="label-eyebrow">Admin agenzia</span>
          <div className="font-display text-2xl font-black mt-2 text-[color:#8B5CF6]">{stats.admins}</div>
        </div>
      </section>

      <div className="sp-card p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <input data-testid="users-search" placeholder="Cerca per nome, email o salone…" className="sp-input !pl-10" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="relative">
          <Filter className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
          <select data-testid="users-role-filter" className="sp-input !pl-10 !py-2 w-auto" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">Tutti i ruoli</option>
            <option value="agency_admin">Agenzia</option>
            <option value="agency_receptionist">Receptionist</option>
            <option value="salon_owner">Titolare</option>
            <option value="staff">Staff</option>
          </select>
        </div>
      </div>

      <div className="sp-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-[color:var(--sp-text-secondary)]">Nessun utente trovato.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-[color:var(--sp-subtle)] text-left">
              <tr className="label-eyebrow">
                <th className="p-4">Utente</th>
                <th className="p-4">Ruolo</th>
                <th className="p-4">Salone</th>
                <th className="p-4">Login</th>
                <th className="p-4">Iscritto il</th>
                <th className="p-4 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className={i % 2 ? "bg-[color:var(--sp-bg)]" : ""}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-display font-black text-sm" style={{ background: ROLE_COLOR[u.role] || "#94A3B8" }}>
                        {(u.name || u.email).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold">{u.name}</div>
                        <div className="text-xs text-[color:var(--sp-text-secondary)]">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold" style={{ background: `${ROLE_COLOR[u.role]}18`, color: ROLE_COLOR[u.role] }}>
                      {u.role === "agency_admin" ? <ShieldCheck className="h-3 w-3" /> : u.role === "salon_owner" ? <Building2 className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-[color:var(--sp-text-secondary)]">
                    {u.tenant_id ? (
                      <Link to={`/admin/tenants/${u.tenant_id}`} className="hover:text-[color:var(--sp-brand)] hover:underline" data-testid={`tenant-link-${u.tenant_id}`}>
                        {u.tenant_name}
                      </Link>
                    ) : "—"}
                  </td>
                  <td className="p-4 text-xs">{AUTH_LABEL[u.auth_provider] || "—"}</td>
                  <td className="p-4 text-xs text-[color:var(--sp-text-secondary)]">{fmtDate(u.created_at)}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => setEditing({ u, mode: "role" })} data-testid={`edit-user-${u.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] text-xs font-bold hover:bg-[color:var(--sp-subtle)]" title="Modifica ruolo">
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button onClick={() => setEditing({ u, mode: "reset" })} data-testid={`reset-user-${u.id}`}
                      className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] text-xs font-bold hover:bg-[color:var(--sp-subtle)]" title="Reset password">
                      <Key className="h-3 w-3" />
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm(`Eliminare ${u.email}?`)) return;
                      await api.delete(`/admin/users/${u.id}`);
                      toast.success("Utente eliminato");
                      load();
                    }} data-testid={`delete-user-${u.id}`}
                      className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-red-400 text-red-600 text-xs font-bold hover:bg-red-50" title="Elimina">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && <CreateUserModal tenants={tenants} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {showProvision && <ProvisionTenantModal onClose={() => setShowProvision(false)} onCreated={() => { setShowProvision(false); load(); }} />}
      {editing && <EditUserModal user={editing.u} mode={editing.mode} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provision new tenant (salone + owner) — invia email con credenziali
// ---------------------------------------------------------------------------
function ProvisionTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    salon_name: "", owner_email: "", owner_name: "",
    plan_tier: "basic", phone: "", address: "", send_credentials: true,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { temp_password, email_sent, tenant, user }
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/tenants", form);
      setResult(r.data);
      toast.success(r.data.email_sent
        ? "Salone creato e credenziali inviate"
        : "Salone creato (email non recapitata — copia la password)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore provisioning");
    } finally { setBusy(false); }
  };

  const copyPwd = async () => {
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      toast.success("Password copiata");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Impossibile copiare"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={result ? null : onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="new-tenant-modal"
        className="sp-card p-6 w-full max-w-lg space-y-3">
        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <h3 className="font-display text-xl font-black inline-flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[color:var(--sp-accent)]" /> Nuovo salone
            </h3>
            <p className="text-xs text-[color:var(--sp-text-secondary)]">
              Crea un nuovo tenant Memento con il primo titolare. Verrà generata una password temporanea sicura.
            </p>
            <div className="grid grid-cols-1 gap-3 pt-2">
              <label className="block"><span className="text-xs font-bold block mb-1">Nome salone *</span>
                <input required className="sp-input" data-testid="new-tenant-salon" value={form.salon_name}
                  onChange={(e) => setForm({ ...form, salon_name: e.target.value })} /></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold block mb-1">Nome titolare *</span>
                  <input required className="sp-input" data-testid="new-tenant-owner-name" value={form.owner_name}
                    onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></label>
                <label className="block"><span className="text-xs font-bold block mb-1">Email titolare *</span>
                  <input required type="email" className="sp-input" data-testid="new-tenant-owner-email" value={form.owner_email}
                    onChange={(e) => setForm({ ...form, owner_email: e.target.value })} /></label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs font-bold block mb-1">Piano</span>
                  <select className="sp-input" data-testid="new-tenant-plan" value={form.plan_tier}
                    onChange={(e) => setForm({ ...form, plan_tier: e.target.value })}>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                  </select></label>
                <label className="block"><span className="text-xs font-bold block mb-1">Telefono</span>
                  <input className="sp-input" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
              </div>
              <label className="block"><span className="text-xs font-bold block mb-1">Indirizzo</span>
                <input className="sp-input" value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
              <label className="flex items-center gap-2 pt-1 cursor-pointer">
                <input type="checkbox" data-testid="new-tenant-send-email" checked={form.send_credentials}
                  onChange={(e) => setForm({ ...form, send_credentials: e.target.checked })} />
                <span className="text-xs font-bold inline-flex items-center gap-1"><Mail className="h-3 w-3" />
                  Invia email di benvenuto con le credenziali</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
              <button type="submit" disabled={busy} data-testid="new-tenant-submit"
                className="px-5 py-2 rounded-full bg-[color:var(--sp-accent)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">
                {busy ? "Provisioning…" : "Crea salone"}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4" data-testid="new-tenant-result">
            <h3 className="font-display text-xl font-black inline-flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-500" /> Salone creato
            </h3>
            <div className="rounded-xl border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)] p-4 space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="label-eyebrow">Nome salone</span>
                <span className="font-bold">{result.tenant.name}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="label-eyebrow">Email</span>
                <span className="font-mono text-sm">{result.user.email}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="label-eyebrow">Password temporanea</span>
                <span className="flex items-center gap-2">
                  <code className="bg-white px-2 py-1 rounded-md font-mono text-sm" data-testid="new-tenant-temp-pwd">{result.temp_password}</code>
                  <button onClick={copyPwd} data-testid="copy-temp-pwd"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[color:var(--sp-brand)] text-white text-xs font-bold hover:opacity-90">
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? "Copiata" : "Copia"}
                  </button>
                </span>
              </div>
            </div>
            <div className={`rounded-lg px-3 py-2 text-xs font-bold ${result.email_sent
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-amber-50 text-amber-800 border border-amber-200"}`}
              data-testid="new-tenant-email-status">
              {result.email_sent
                ? "Email di benvenuto inviata correttamente."
                : "Email non recapitata — copia la password sopra e comunicala al titolare in modo alternativo."}
            </div>
            <p className="text-[11px] text-[color:var(--sp-text-muted)]">
              La password è memorizzata solo in forma cifrata. Comunicala <b>subito</b>: non è più recuperabile da qui.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onCreated} data-testid="new-tenant-done"
                className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90">Chiudi</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateUserModal({ tenants, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "salon_owner", tenant_id: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.role !== "agency_admin" && form.role !== "agency_receptionist" && !form.tenant_id) { toast.error("Seleziona un salone"); return; }
    setBusy(true);
    try {
      const payload = { ...form, tenant_id: (form.role === "agency_admin" || form.role === "agency_receptionist") ? null : form.tenant_id };
      await api.post("/admin/users", payload);
      toast.success("Utente creato");
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-user-modal"
        className="sp-card p-6 w-full max-w-md space-y-3">
        <h3 className="font-display text-xl font-black">Nuovo utente</h3>
        <label className="block"><span className="text-xs font-bold block mb-1">Email *</span>
          <input required type="email" className="sp-input" value={form.email} data-testid="new-user-email"
            onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="block"><span className="text-xs font-bold block mb-1">Nome *</span>
          <input required className="sp-input" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="block"><span className="text-xs font-bold block mb-1">Password (min 8 caratteri + 1 cifra) *</span>
          <input required type="password" minLength={8} className="sp-input" value={form.password} data-testid="new-user-password"
            onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
        <label className="block"><span className="text-xs font-bold block mb-1">Ruolo</span>
          <select className="sp-input" value={form.role} data-testid="new-user-role"
            onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="salon_owner">Titolare salone</option>
            <option value="staff">Staff</option>
            <option value="agency_admin">Admin agenzia</option>
            <option value="agency_receptionist">Receptionist (agenzia)</option>
          </select></label>
        {form.role === "agency_receptionist" && (
          <p className="text-xs text-[color:var(--sp-text-muted)]" data-testid="new-user-receptionist-note">
            Riceverà i permessi predefiniti della cartella Receptionist. I saloni si assegnano
            successivamente dalle assegnazioni.
          </p>
        )}
        {form.role !== "agency_admin" && form.role !== "agency_receptionist" && (
          <label className="block"><span className="text-xs font-bold block mb-1">Salone</span>
            <select required className="sp-input" value={form.tenant_id} data-testid="new-user-tenant"
              onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
              <option value="">— Seleziona —</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></label>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="new-user-submit"
            className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">Crea</button>
        </div>
      </form>
    </div>
  );
}

function EditUserModal({ user, mode, onClose, onSaved }) {
  const [role, setRole] = useState(user.role);
  const [name, setName] = useState(user.name || "");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [agencyPerms, setAgencyPerms] = useState(null);   // null = non caricato
  const isAgencyRole = role === "agency_receptionist" || role === "agency_admin";

  useEffect(() => {
    if (mode !== "role") return;
    if (user.role !== "agency_receptionist" && user.role !== "agency_admin") return;
    api.get(`/admin/agency-users/${user.id}/permissions`)
      .then((r) => setAgencyPerms(r.data?.overrides ?? r.data?.defaults ?? []))
      .catch(() => setAgencyPerms([]));
  }, [mode, user.id, user.role]);

  const allKeys = RECEPTIONIST_PERM_LIST.map((p) => p.key);
  // Cambio ruolo → default del nuovo ruolo (poi override manuale).
  const changeRole = (next) => {
    setRole(next);
    if (next === "agency_receptionist") setAgencyPerms(allKeys);
    else if (next === "agency_admin") setAgencyPerms([]);
  };
  const togglePerm = (k) => setAgencyPerms((cur) => {
    const list = cur || [];
    return list.includes(k) ? list.filter((x) => x !== k) : [...list, k];
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let payload;
      if (mode === "reset") payload = { new_password: newPassword };
      else {
        payload = { role, name };
        if (role === "agency_receptionist" && agencyPerms) payload.agency_permissions = agencyPerms;
      }
      await api.patch(`/admin/users/${user.id}`, payload);
      toast.success("Salvato");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-user-modal"
        className="sp-card p-6 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
        <h3 className="font-display text-xl font-black">{mode === "reset" ? "Reset password" : "Modifica utente"}</h3>
        <div className="text-sm text-[color:var(--sp-text-muted)]">{user.email}</div>
        {mode === "reset" ? (
          <label className="block"><span className="text-xs font-bold block mb-1">Nuova password</span>
            <input type="password" minLength={8} required className="sp-input" value={newPassword} data-testid="edit-user-pwd"
              onChange={(e) => setNewPassword(e.target.value)} />
            <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">Tutte le sessioni attive verranno invalidate.</p>
          </label>
        ) : (
          <>
            <label className="block"><span className="text-xs font-bold block mb-1">Nome</span>
              <input className="sp-input" value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label className="block"><span className="text-xs font-bold block mb-1">Ruolo</span>
              <select className="sp-input" value={role} onChange={(e) => changeRole(e.target.value)} data-testid="edit-user-role">
                <option value="salon_owner">Titolare</option>
                <option value="staff">Staff</option>
                <option value="agency_admin">Admin agenzia</option>
                <option value="agency_receptionist">Receptionist</option>
              </select></label>

            {isAgencyRole && (
              <div className="rounded-xl border border-[color:var(--sp-border)] p-3 space-y-2" data-testid="agency-access-box">
                {role === "agency_receptionist" && <AdminChannelsView receptionistId={user.id} />}
                <div className="text-xs font-black inline-flex items-center gap-2">
                  <Headset className="h-3.5 w-3.5 text-[color:var(--sp-brand)]" /> Accessi · Receptionist
                </div>
                {role === "agency_admin" ? (
                  <p className="text-xs text-[color:var(--sp-text-muted)]" data-testid="agency-access-admin-note">
                    L'admin agenzia ha accesso completo all'area Agency.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {RECEPTIONIST_PERM_LIST.map((p) => (
                      <label key={p.key} data-testid={`agency-perm-${p.key}`}
                        className="flex items-center gap-2 text-sm cursor-pointer rounded-lg px-2 py-1 hover:bg-[color:var(--sp-subtle)]">
                        <input type="checkbox" checked={(agencyPerms || []).includes(p.key)}
                          onChange={() => togglePerm(p.key)} />
                        {p.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold hover:bg-[color:var(--sp-subtle)]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="edit-user-save"
            className="px-5 py-2 rounded-full bg-[color:var(--sp-brand)] text-white text-sm font-bold hover:opacity-90 disabled:opacity-50">Salva</button>
        </div>
      </form>
    </div>
  );
}
